import React, { useState, useMemo, useCallback, memo, useEffect } from 'react';
import { Button } from '@cloudscape-design/components';
import { isEqual } from 'lodash';
import './ExpandableTable.css';

// Table row interface definition
export interface TableRow {
  [key: string]: any | React.ReactNode;
  expandedContent?: React.ReactNode | string;
  editComponent?: (columnKey: string) => React.ReactNode;
  bussnessUnit?: string;
  updated?: string;
  action?: React.ReactNode;
  status?: React.ReactNode;
}

export const cancelEditing = (index: number) => {
  if (index !== -1) {
    // Access the ExpandableTable's setEditingRow through the ref
    document.dispatchEvent(new CustomEvent('cancelEditing', { detail: { index } }));
  }
};

export const collapseALLView = () => {
  // Access the ExpandableTable's setEditingRow through the ref
  document.dispatchEvent(new CustomEvent('collapseALLView', { detail: {} }));
};
// Props interface for the ExpandableTable component
interface ExpandableTableProps<T extends TableRow> {
  data: T[];
  columns: Column<T>[];
  expandableContentKey?: keyof T;
  defaultExpanded?: boolean;
}

// Column interface definition
export interface Column<T = any> {
  key: keyof T;
  header: string;
  editable?: boolean;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
}

function ExpandableTable<T extends TableRow = TableRow>({
  data,
  columns,
  expandableContentKey = 'expandedContent',
  defaultExpanded = false,
}: ExpandableTableProps<T>) {
  // State for tracking expanded and editing rows
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [editingRow, setEditingRow] = useState<Record<string, number>>({});

  // Effect to handle changes in defaultExpanded prop
  useEffect(() => {
    if (defaultExpanded) {
      const newSet = new Set<number>();
      newSet.add(0);
      setExpandedRows(newSet);
    } else {
      setExpandedRows(new Set());
    }
  }, [defaultExpanded]);

  // Memoized handler for toggling row expansion
  const toggleRow = useCallback((index: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (prev.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  // Listen for external cancel editing events
  React.useEffect(() => {
    const handleCancelEditing = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.index !== undefined) {
        setEditingRow({});
      }
    };

    const handleCollapseALLView = (event: Event) => {
      setExpandedRows(new Set());
    };

    document.addEventListener('cancelEditing', handleCancelEditing);
    document.addEventListener('collapseALLView', handleCollapseALLView);
    return () => {
      document.removeEventListener('cancelEditing', handleCancelEditing);
      document.removeEventListener('collapseALLView', handleCollapseALLView);
    };
  }, []);

  // Memoized function to check if row has expandable content
  const hasExpandableContent = useCallback(
    (row: T): boolean => {
      return Boolean(row[expandableContentKey]);
    },
    [expandableContentKey],
  );

  // Memoized cell renderer
  const renderCell = useCallback(
    (value: T[keyof T], column: Column<T>, row: T, rowIndex: number) => {
      if (
        editingRow[column.key as string] === rowIndex &&
        row.editComponent &&
        column.editable
      ) {
        return row.editComponent(column.key as string);
      }

      if (column.render) {
        return column.render(value, row);
      }

      if (React.isValidElement(value)) {
        return value;
      }

      if (column.editable) {
        return (
          <div
            className="editable-cell"
            onClick={() => {
              setEditingRow({
                [column.key]: rowIndex,
              });
            }}
            role="button"
            tabIndex={0}
          >
            {value}
          </div>
        );
      }

      return value;
    },
    [editingRow],
  );

  // Memoized table headers
  const tableHeaders = useMemo(
    () => (
      <tr>
        <th className="expand-column" aria-label="Expand column"></th>
        {columns.map((column) => (
          <th className="expandable-table-th" key={String(column.key)}>
            {column.header}
          </th>
        ))}
      </tr>
    ),
    [columns],
  );

  return (
    <div className="table-container">
      <table className="expandable-table" role="grid">
        <thead>{tableHeaders}</thead>
        <tbody>
          {data.map((row, index) => (
            <React.Fragment key={index}>
              <tr>
                <td className="expandable-table-td-arrow">
                  {hasExpandableContent(row) && (
                    <Button
                      iconName={expandedRows.has(index) ? 'angle-up' : 'angle-down'}
                      variant="icon"
                      onClick={() => toggleRow(index)}
                    />
                  )}
                </td>
                {columns.map((column) => (
                  <td className="expandable-table-td" key={String(column.key)}>
                    {renderCell(row[column.key], column, row, index)}
                  </td>
                ))}
              </tr>
              {hasExpandableContent(row) && expandedRows.has(index) && (
                <tr className="expanded-row">
                  <td className="expanded-row-td" colSpan={columns.length + 1}>
                    <div className="expanded-content">{row[expandableContentKey]}</div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Wrap the component with memo to optimize re-renders
const MemoizedExpandableTable = memo(ExpandableTable, (prevProps, nextProps) => {
  // Custom comparison function using lodash's isEqual for deep comparison
  return (
    isEqual(prevProps.data, nextProps.data) &&
    isEqual(prevProps.defaultExpanded, nextProps.defaultExpanded) &&
    isEqual(prevProps.columns, nextProps.columns) &&
    prevProps.expandableContentKey === nextProps.expandableContentKey
  );
}) as typeof ExpandableTable;

export default MemoizedExpandableTable;
